import { Accordion, Card, Link, Text } from '@vkontakte/vkui';
import freemiumModalAddUsersImage from 'img/family-content/freemium-modal/freemium-modal-add-users.png';
import freemiumModalAddUsersImageDark from 'img/family-content/freemium-modal/freemium-modal-add-users-dark.png';
import freemiumModalCloudImage from 'img/family-content/freemium-modal/freemium-modal-cloud.png';
import freemiumModalCloudImageDark from 'img/family-content/freemium-modal/freemium-modal-cloud-dark.png';
import freemiumModalFamilyFoldersImage from 'img/family-content/freemium-modal/freemium-modal-family-folders.png';
import freemiumModalFamilyFoldersImageDark from 'img/family-content/freemium-modal/freemium-modal-family-folders-dark.png';
import freemiumModalNoAddImage from 'img/family-content/freemium-modal/freemium-modal-no-add.png';
import freemiumModalNoAddImageDark from 'img/family-content/freemium-modal/freemium-modal-no-add-dark.png';
import freemiumModalUnlimitedSpaceImage from 'img/family-content/freemium-modal/freemium-modal-unlimited-space.png';
import freemiumModalUnlimitedSpaceImageDark from 'img/family-content/freemium-modal/freemium-modal-unlimited-space-dark.png';
import freemiumModalUploadImage from 'img/family-content/freemium-modal/freemium-modal-upload.png';
import freemiumModalUploadImageDark from 'img/family-content/freemium-modal/freemium-modal-upload-dark.png';
import React from 'react';
import { isDarkTheme } from 'reactApp/appHelpers/featuresHelpers';
import { closeCommonWhatsNewDialog, renderCommonWhatsNewDialog } from 'reactApp/components/WhatsNewDialog/WhatsNewDialog.helpers';
import { historyPush } from 'reactApp/modules/router/router.module';
import { store as reduxStore } from 'reactApp/store';

import { renderAutoUploadModal } from '../AutoUploadModal';
import { sendFreemiumAboutModalAnalytics } from './FreemiumAboutModal.analytics';
import styles from './FreemiumAboutModal.css';

interface FeatureCard {
    text: React.ReactNode;
    hasLink: boolean;
    linkText: string | null;
    image: string;
    onLinkClick: (() => void) | null;
}

const FEATURES_CARDS: FeatureCard[] = [
    {
        text: (
            <>
                Безлимитное место
                <br />
                для фото с телефона
            </>
        ),
        hasLink: true,
        linkText: 'Как работает',
        onLinkClick: null,
        image: isDarkTheme ? freemiumModalUnlimitedSpaceImageDark : freemiumModalUnlimitedSpaceImage,
    },
    {
        text: (
            <>
                Возможность пригласить
                <br />
                ещё 3 близких без доплаты
            </>
        ),
        hasLink: true,
        linkText: 'Добавить',
        onLinkClick: null,
        image: isDarkTheme ? freemiumModalAddUsersImageDark : freemiumModalAddUsersImage,
    },
    {
        text: (
            <>
                +256 ГБ для того,
                <br />
                что грузите с компьютера
            </>
        ),
        hasLink: false,
        linkText: null,
        onLinkClick: null,
        image: isDarkTheme ? freemiumModalCloudImageDark : freemiumModalCloudImage,
    },
    {
        text: (
            <>
                Безрекламность — никаких
                <br />
                баннеров в Облаке и Почте
            </>
        ),
        hasLink: false,
        linkText: null,
        onLinkClick: null,
        image: isDarkTheme ? freemiumModalNoAddImageDark : freemiumModalNoAddImage,
    },
    {
        text: (
            <>
                Загрузка файлов до 100 ГБ:
                <br />
                например, любимый фильм
            </>
        ),
        hasLink: false,
        linkText: null,
        onLinkClick: null,
        image: isDarkTheme ? freemiumModalUploadImageDark : freemiumModalUploadImage,
    },
    {
        text: (
            <>
                Семейные папки и альбомы
                <br />
                для общих файлов
            </>
        ),
        hasLink: false,
        linkText: null,
        onLinkClick: null,
        image: isDarkTheme ? freemiumModalFamilyFoldersImageDark : freemiumModalFamilyFoldersImage,
    },
];

interface FAQItem {
    question: string;
    answer: string | React.ReactNode;
}

const FAQ_ITEMS: FAQItem[] = [
    {
        question: 'Не будут ли списываться деньги с карты?',
        answer: 'Нет, поскольку для пробного периода не нужно привязывать карту',
    },
    {
        question: 'Что будет с файлами, когда закончится действие пробного периода?',
        answer: 'Фото и видео с телефона, загруженные с безлимитом, начнут учитываться в рамках бесплатного объёма (8 ГБ)',
    },
    {
        question: 'Как продлить подписку и сколько она будет стоить?',
        answer: (
            <>
                Подписку можно продлить и во время пробного периода, и после. <br />
                Первые три месяца семейный тариф на 256 ГБ стоит 349 ₽, далее — 449 ₽ в месяц
            </>
        ),
    },
];

export const renderFreemiumAboutModal = (): void => {
    sendFreemiumAboutModalAnalytics('show');

    const cardsWithHandlers = FEATURES_CARDS.map((card, index) => {
        if (index === 0 && card.hasLink) {
            return {
                ...card,
                onLinkClick: () => {
                    sendFreemiumAboutModalAnalytics('click', 'how_work');
                    closeCommonWhatsNewDialog();
                    renderAutoUploadModal({ reopenFreemiumAboutModal: true, renderFreemiumAboutModal });
                },
            };
        }
        if (index === 1 && card.hasLink) {
            return {
                ...card,
                onLinkClick: () => {
                    sendFreemiumAboutModalAnalytics('click', 'add_member');
                    closeCommonWhatsNewDialog();
                    reduxStore.dispatch(historyPush({ id: '/family' }));
                },
            };
        }
        return card;
    });

    const featuresContent = (
        <div className={styles.featuresGrid}>
            {cardsWithHandlers.map((card, index) => (
                <Card key={index} className={styles.featureCard}>
                    <img src={card.image} alt="" className={styles.featureImage} />
                    <div className={styles.featureContent}>
                        <Text className={styles.featureText}>{card.text}</Text>
                        {card.hasLink && card.linkText && (
                            <Link
                                className={styles.featureLink}
                                onClick={(e) => {
                                    e.preventDefault();
                                    if (card.onLinkClick) {
                                        card.onLinkClick();
                                    }
                                }}
                            >
                                {card.linkText}
                            </Link>
                        )}
                    </div>
                </Card>
            ))}
        </div>
    );

    const faqContent = (
        <div className={styles.faqSection}>
            <Text className={styles.faqTitle}>Частые вопросы:</Text>
            <div className={styles.faqList}>
                {FAQ_ITEMS.map((item, index) => (
                    <Accordion key={index}>
                        <Accordion.Summary>{item.question}</Accordion.Summary>
                        <div className={styles.faqAnswer}>
                            <Text>{item.answer}</Text>
                        </div>
                    </Accordion>
                ))}
            </div>
        </div>
    );

    const content = (
        <>
            {featuresContent}
            {faqContent}
        </>
    );

    renderCommonWhatsNewDialog({
        title: 'Что доступно в подписке:',
        titleClassName: styles.modalTitle,
        content,
        closable: true,
        closeOnEscape: true,
        closeOnDimmerClick: true,
        hideImage: true,
        imageUrl: '',
        onClose: () => {
            sendFreemiumAboutModalAnalytics('close');
        },
    });
};
